<template>
  <div v-if="!isLoading" class="p-4 max-w-xl mx-auto">
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg />
    </div>
    <div class="flex flex-col justify-center items-center space-y-1">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Reference Number
      </h2>
      <h4 class="text-sm text-zinc-500">#{{ transactionId }}</h4>
      <span
        class="text-sm px-3.5 py-2 rounded-full font-medium"
        :class="statusClass(itemData.status)"
      >
        {{ modifiedStatus(itemData.status) }}
      </span>
    </div>
    <div
      class="relative bg-white border border-gray-200 rounded-2xl shadow-lg my-4 overflow-hidden"
    >
      <!-- Left cutout -->
      <div
        class="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Right cutout -->
      <div
        class="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Top Section -->
      <div
        class="flex flex-col gap-3 justify-center items-center py-8 bg-gradient-to-r from-emerald-50 to-white"
      >
        <span class="px-14">
          <img
            src="../../assets/images/consultation.gif"
            alt=""
            class="h-24 w-24"
          />
        </span>
      </div>

      <!-- Divider -->
      <div class="border-t border-dashed border-gray-300"></div>

      <!-- Bottom Section -->
      <div
        class="flex gap-2 justify-center flex-col items-center py-4 bg-gray-50"
      >
        <span class="text-xs text-zinc-400">
          <h5>{{ dateFormat(itemData.createdat) }}</h5>
        </span>
      </div>
    </div>
    <div>
      <h3 class="text-base text-zinc-700 font-medium">Transaction Details</h3>
    </div>

    <div
      class="mt-4 flex flex-col gap-3 rounded-md p-5 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
    >
      <div class="flex gap-3 items-center">
        <img
          class="w-16 h-16 object-cover rounded-full shadow"
          :src="urlAvatar(itemData.profile)"
          alt=""
        />
        <span>
          <h3 class="font-medium text-base">{{ itemData.doctor }}</h3>
          <h4 class="text-sm text-zinc-400">
            {{ itemData.specialization }}
          </h4>
        </span>
      </div>
    </div>
    <div class="my-2 flex gap-2">
      <div
        class="flex-1 items-center rounded-md p-3 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-sm text-zinc-500 font-medium">Duration</h3>
          <div class="flex gap-2 items-center">
            <TimeSvg />

            <h1 class="text-base text-zinc-800">
              {{
                getDurationTime(
                  itemData.startTimeSession,
                  itemData.endTimeSession
                )
              }}
            </h1>
          </div>
        </div>
      </div>
      <div
        class="flex-1 items-center rounded-md p-3 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-sm text-zinc-500 font-medium"></h3>
          <div class="flex gap-2 items-center">
            <CashSvg />
            <h1 class="text-base text-zinc-800"></h1>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-4 space-y-4">
      <!-- Cancel Button (Long Press with Progress) -->
      <div
        class="w-full my-2 relative flex flex-col gap-2"
        v-if="itemData.status === 'completed'"
      >
        <button
          @click="downloadHandler()"
          class="relative w-full py-3 rounded-lg bg-orange-500 text-white font-semibold tracking-wide hover:bg-orange-600 active:bg-orange-700 transition overflow-hidden"
        >
          <span class="relative z-10">E-Prescription</span>
        </button>
        <button
          @click="downloadTestProcedureHandler()"
          class="relative w-full py-3 rounded-lg bg-orange-500 text-white font-semibold tracking-wide hover:bg-orange-600 active:bg-orange-700 transition overflow-hidden"
        >
          <span class="relative z-10">Laboratory Referral</span>
        </button>
        <button
          @click="downloadMedicalCertificationHandler()"
          class="relative w-full py-3 rounded-lg bg-orange-500 text-white font-semibold tracking-wide hover:bg-orange-600 active:bg-orange-700 transition overflow-hidden"
        >
          <span class="relative z-10">Medical Certification</span>
        </button>
      </div>
      <div
        class="pb-6 text-zinc-400 flex flex-col gap-2 justify-center items-center"
      >
        <span class="text-sm">Customer Services Hotlines</span>
        <span class="text-xs"
          >0922-822-1033-35, 0917-851-8645, 0998-586-9439, 09171326517</span
        >
      </div>
    </div>
  </div>
  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script>
import BackSvg from "@/components/svgs/BackSvg.vue";
import { computed, inject, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import TimeSvg from "@/components/svgs/TimeSvg.vue";
import CashSvg from "@/components/svgs/CashSvg.vue";
import pdfGenerateHandler from "@/utils/prescription";
import pdfReferralGenerateHandler from "@/utils/testreferral";
export default {
  components: {
    TimeSvg,
    CashSvg,
    BackSvg,
  },
  setup() {
    const axios = inject("$axios");
    const router = inject("$router");
    const moment = inject("$moment");
    const route = useRoute();
    const transactionId = ref();
    const itemData = ref([]);
    const isLoading = ref(false);
    const { exportPDF } = pdfGenerateHandler();
    const { exportReferralPDF } = pdfReferralGenerateHandler();

    onMounted(() => {
      transactionId.value = route.params.id;
      currentQueuingHandler();
    });

    const currentQueuingHandler = async () => {
      try {
        isLoading.value = true;
        const { data } = await axios.get(
          `/api/patient/transaction/detail/${transactionId.value}`
        );
        itemData.value = data.data;
        if (data.data === undefined) {
          router.push({ name: "NotFound" });
        }

        setTimeout(() => {
          isLoading.value = false;
        }, 800);
      } catch (error) {
        console.log(error);
      }
    };

    const queuingMessage = computed(() => {
      let status = itemData.value.status;
      let result;

      if (status === "in-line") {
        result = itemData.value.spot_number;
      } else if (status === "admitted") {
        result = "Your turn";
      } else if (status === "ringing") {
        result = "The doctor has joined the video room.";
      } else if (status === "in-session") {
        result = "You are currently in session";
      } else if (status === "completed") {
        result = "The consultation was completed.";
      }
      return result;
    });

    const dateFormat = (val) => {
      return moment(val).format("LLL");
    };
    const urlAvatar = (profile) => {
      return `https://dev-api.iwcsrvr.com/api/portal/patient/profile?filename=${profile}`;
    };
    const getDurationTime = (startTime, endTime) => {
      let durationTime;

      if (startTime) {
        const start = moment(startTime);
        const end = moment(endTime);

        // Format as mm:ss
        const duration = moment.duration(end.diff(start));
        const formatted = moment.utc(duration.asMilliseconds()).format("mm:ss");
        durationTime = `${formatted} minutes`;
      } else {
        durationTime = "-";
      }

      return durationTime;
    };
    const modifiedStatus = (status) => {
      let result;
      if (status === "completed") {
        result = "Completed";
      } else if (status === "cancel") {
        result = "Cancel";
      } else if (status === "in-line") {
        result = "Pending";
      } else {
        result = "Denied";
      }

      return result;
    };

    const statusClass = (status) => {
      if (status === "completed") return "bg-green-100 text-green-700";
      if (status === "cancel") return "bg-red-100 text-red-700";
      if (status === "in-line") return "bg-gray-100 text-gray-700";
      return "bg-orange-100 text-orange-700";
    };
    const downloadHandler = async () => {
      try {
        const pdfUrl = `https://archive.iwcdeped.com/PRESCRIPTION_${transactionId.value}.pdf`;
        window.open(pdfUrl + "#view", "_blank");
      } catch (error) {
        console.log(error);
      }
    };

    const downloadTestProcedureHandler = async () => {
      try {
        const pdfUrl = `https://archive.iwcdeped.com/LAB_TEST_${transactionId.value}.pdf`;
        window.open(pdfUrl + "#view", "_blank");
      } catch (error) {
        console.log(error);
      }
    };
    const downloadMedicalCertificationHandler = async () => {
      try {
        const pdfUrl = `https://archive.iwcdeped.com/MEDICAL_CERTIFICATION_${transactionId.value}.pdf`;
        window.open(pdfUrl + "#view", "_blank");
      } catch (error) {
        console.log(error);
      }
    };
    return {
      transactionId,
      currentQueuingHandler,
      itemData,
      queuingMessage,
      dateFormat,
      isLoading,
      urlAvatar,
      getDurationTime,
      modifiedStatus,
      statusClass,
      downloadHandler,
      downloadTestProcedureHandler,
      exportPDF,
      exportReferralPDF,
      downloadMedicalCertificationHandler,
    };
  },
};
</script>
